import type {
    ToastType,
    ToastDefaultStatus,
    ToastNotificationStatus,
    ToastTypeStatusIconAttributes,
    CreateToastArgs,
    ShowToastArgs
} from "./types";

import type AtlasToastContainer from "../atlas-toast-container/atlas-toast-container";

import { isEmptyValue } from "@/helpers/validations";

const TOAST_CONTAINER_COMPONENT = "atlas-toast-container";

const iconStatus: ToastTypeStatusIconAttributes = {
    default: {
        success: {
            icon: "check",
            theme: "success"
        },
        error: {
            icon: "alert-triangle",
            theme: "danger"
        },
        warning: {
            icon: "alert-circle",
            theme: "warning"
        },
        info: {
            icon: "info",
            theme: "info"
        },
        none: {
            icon: "info",
            theme: "info"
        }
    },
    notification: {
        "success": {
            icon: "check",
            theme: "success"
        },
        "error": {
            icon: "x",
            theme: "danger"
        },
        "attention": {
            icon: "alert",
            theme: "warning"
        },
        "in-progress": {
            icon: "clock",
            theme: "info"
        },
        "none": {
            icon: "info",
            theme: "info"
        }
    }
};

const isToastDefaultStatus = (status: string): status is ToastDefaultStatus => {
    return ["success", "error", "warning", "info", "none"].includes(status);
};

const isToastNotificationStatus = (status: string): status is ToastNotificationStatus => {
    return ["success", "in-progress", "attention", "error", "none"].includes(status);
};

const createToastContainerIfNeeded = () => {
    let container = document.querySelector(TOAST_CONTAINER_COMPONENT) as AtlasToastContainer;

    if (container) return container;

    container = document.createElement(TOAST_CONTAINER_COMPONENT);

    document.body.append(container);

    return container;
};

const getIconAttributes = (status: ToastDefaultStatus | ToastNotificationStatus, type: ToastType) => {
    let iconAttributes;

    if (type === "default" && isToastDefaultStatus(status)) {
        iconAttributes = iconStatus["default"][status];
    } else if (isToastNotificationStatus(status)) {
        iconAttributes = iconStatus["notification"][status];
    }

    return iconAttributes;
};

const createToast = ({
    status = "none",
    icon,
    type = "default",
    header,
    headerInfo,
    content,
    href,
    isExternalLink= false,
    hasAction = false,
    primaryActionButton,
    secondaryActionButton
}: CreateToastArgs) => {
    const toast = document.createElement("atlas-toast");

    if (!!icon) toast.setAttribute("icon", icon);

    if (!!status && status !== "none") {
        const iconAttributes = getIconAttributes(status, type);

        if (type === "notification") {
            toast.setAttribute("overlay-icon", iconAttributes.icon);
            toast.setAttribute("overlay-icon-theme", iconAttributes.theme);
        } else {
            toast.setAttribute("icon", iconAttributes.icon);
            toast.setAttribute("icon-theme", iconAttributes.theme);
        }
    }

    if (!!header) toast.setAttribute("header", header);

    if (!!headerInfo) toast.setAttribute("header-info", headerInfo);

    if (!!href) {
        toast.setAttribute("href", href);
        toast.toggleAttribute("is-external-link", isExternalLink);
    }

    if (hasAction) toast.toggleAttribute("has-action", hasAction);

    toast.innerHTML = content;

    if (primaryActionButton && Object.keys(primaryActionButton).length > 0) {
        const primaryButton = document.createElement("atlas-button");
        primaryButton.setAttribute("size", "sm");
        primaryButton.setAttribute("id", primaryActionButton.id);
        primaryButton.setAttribute("class", primaryActionButton.class);
        primaryButton.setAttribute("description", primaryActionButton.description);
        primaryButton.setAttribute("href", primaryActionButton.href);
        primaryButton.setAttribute("icon", primaryActionButton.icon);
        primaryButton.toggleAttribute("is-external-link", primaryActionButton.isExternalLink);
        primaryButton.setAttribute("slot", "actions");

        toast.appendChild(primaryButton);
    }

    if (secondaryActionButton && Object.keys(secondaryActionButton).length > 0) {
        const secondaryButton = document.createElement("atlas-button");
        secondaryButton.setAttribute("size", "sm");
        secondaryButton.setAttribute("type", "ghost");
        secondaryButton.setAttribute("id", secondaryActionButton.id);
        secondaryButton.setAttribute("class", secondaryActionButton.class);
        secondaryButton.setAttribute("description", secondaryActionButton.description);
        secondaryButton.setAttribute("href", secondaryActionButton.href);
        secondaryButton.setAttribute("icon", secondaryActionButton.icon);
        secondaryButton.toggleAttribute("is-external-link", secondaryActionButton.isExternalLink);
        secondaryButton.setAttribute("slot", "actions");

        toast.appendChild(secondaryButton);
    }

    return toast;
};

/**
 * Exibe um toast flutuante na tela.
 * @param toastElement Um elemento de toast já criado previamente.
 * @param type Indica o tipo de toast a ser exibido. Pode ser "default" ou "notification".
 * @param status O status do toast. Deve ser uma das strings ("success" | "error" | "warning" | "info") ou ("success" | "in-progress" | "attention" | "error") dependendo do tipo.
 * @param icon O ícone que será exibido no toast. O status do toast irá sobrescrever este ícone se for fornecido, com exceção do tipo "notification".
 * @param header O título do toast.
 * @param headerInfo O texto que será exibido ao lado do título.
 * @param content O conteúdo do toast.
 * @param href URL para onde o usuário será redirecionado ao clicar no toast.
 * @param isExternalLink Indica se o link é externo.
 * @param hasAction Indica se o toast tem ação de clique, para que o evento seja emitido ao clicar nele.
 * @param primaryActionButton Um objeto com as propriedades do botão de ação primária, como id, class, description, href, icon e isExternalLink.
 * @param secondaryActionButton Um objeto com as propriedades do botão de ação secundária, como id, class, description, href, icon e isExternalLink.
 * @param duration O tempo em milissegundos que o toast ficará visível antes de ser fechado automaticamente. O padrão é 5000ms.
 */
export function showToast({
    toastElement,
    status,
    icon,
    type = "default",
    header,
    headerInfo,
    content,
    href,
    isExternalLink = false,
    hasAction = false,
    primaryActionButton,
    secondaryActionButton,
    duration = 5000
}: ShowToastArgs) {
    const container = createToastContainerIfNeeded();

    let toast = toastElement;

    if (!toast) {
        toast = createToast({
            status,
            icon,
            type,
            header,
            headerInfo,
            content,
            href,
            isExternalLink,
            hasAction,
            primaryActionButton,
            secondaryActionButton
        });
    }

    if (isEmptyValue(primaryActionButton) && isEmptyValue(secondaryActionButton)) {
        toast.setAttribute("data-duration", duration.toString());
    }

    container.addToast(toast);
}
